<!--
  - Copyright (c) 2023-2024. Haodong JU
  -->

<template>
  <div>
    <page-header class="py-4 pb-6">
      {{ $t('ProductAndMaterialRelation') }}
    </page-header>
    <v-card
      class="pa-2"
      elevation="0"
      style="position: sticky;top: 0;z-index: 1"
    >
      <v-text-field
        v-model="searchWord"
        :placeholder="$t('searchNameCode')"
        append-icon="mdi-magnify"
        hide-details
        outlined
      />
    </v-card>
    <loading-provider :loading="loading">
      <div
        class="py-2"
        style="display: grid;grid-template-columns:repeat(auto-fill,minmax(128px,1fr));grid-gap: 12px"
      >
        <v-card
          v-for="dish in filteredDishList"
          :key="dish.id"
          class="pa-4 d-flex flex-column"
          :height="112"
          color="grey lighten-5"
          elevation="0"
          @click="configDish(dish)"
        >
          <div
            class="text-body-1 font-weight-black"
            style="
  overflow: hidden;
   text-overflow: ellipsis;
   display: -webkit-box;
   -webkit-line-clamp: 2; /* number of lines to show */
           line-clamp: 2;
   -webkit-box-orient: vertical;"
          >
            {{ dish.code }}.{{ dish.dishName }}
          </div>
          <v-spacer />
          <div
            style="width: fit-content"
            class="text-body-1 d-flex flex-wrap rounded px-2 align-self-end"
            :class="dish.resource.length===0?'error white--text':'grey lighten-3'"
          >
            {{ dish.resource.length }}&times;
          </div>
        </v-card>
      </div>
    </loading-provider>
    <nice-dialog
      v-model="showDetailDialog"
    >
      <v-card
        v-if="selectedDish"
        class="pa-6"
        elevation="0"
      >
        <page-sub-header>{{ $t('productMaterialEdit') }}</page-sub-header>
        <div
          class="py-2 mt-0"
          style="display: grid;grid-template-columns: repeat(3,minmax(0,1fr))"
        >
          <div>
            <div class="text-subtitle-1 font-weight-bold">
              {{ $t('code') }}
            </div>
            <div class="text-body-1">
              {{ selectedDish.code }}
            </div>
          </div>
          <div>
            <div class="text-subtitle-1 font-weight-bold">
              {{ $t('name') }}
            </div>
            <div class="text-body-1">
              {{ selectedDish.dishName }}
            </div>
          </div>
          <div>
            <div class="text-subtitle-1 font-weight-bold">
              {{ $t('price') }}
            </div>
            <div class="text-body-1">
              {{ selectedDish.price | priceDisplay }}
            </div>
          </div>
        </div>
        <div class="d-flex">
          <div class="text-subtitle-1 font-weight-bold mt-10">
            {{ $t('rawMaterialInfo') }}
          </div>
        </div>
        <div
          class="py-2 mt-0"
          style="
          display: grid;
          grid-template-columns: 1fr;
          grid-gap: 8px"
        >
          <v-card
            v-for="r in currentResourceList"
            :key="r.id"
            class="pa-2 px-4 d-flex align-center text-body-1"
            color="grey lighten-4"
            elevation="0"
            @click="editInfo(r)"
          >
            <div>{{ r.storageItem.name }}</div>
            <v-spacer />
            &times;
            <div class="mx-2">
              {{ r.unitDisplay }}
            </div>
            <v-btn
              icon
              @click.stop="removeResourceFromDish(r)"
            >
              <v-icon size="24">
                mdi-delete-circle
              </v-icon>
            </v-btn>
          </v-card>
          <div class="mt-4">
            <v-btn
              outlined
              style="background: white"
              @click="addAResource"
            >
              <v-icon left>
                mdi-plus-circle
              </v-icon>
              {{ $t('addOtherRawMaterial') }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </nice-dialog>
    <nice-dialog
      v-model="showResourcePicker"
    >
      <v-container :class="!$vuetify.breakpoint.lgAndUp ? 'px-4' : 'px-6'">
        <page-sub-header v-if="selectedDish">
          {{ $t('addRawMaterial', { selectedDish: selectedDish.dishName }) }}
        </page-sub-header>
        <v-autocomplete
          v-model="selectedResource"
          :items="resourceList"
          :placeholder="$t('chooseOneRawMaterial')"
          item-text="name"
          outlined
          return-object
        />
        <v-text-field
          v-model="consumeCount"
          :placeholder="$t('inputOneNumber')"
          outlined
        />
        <v-autocomplete
          v-if="selectedResource"
          v-model="selectedUnit"
          :items="unitList"
          :placeholder="$t('chooseOneUnit')"
          item-text="name"
          outlined
          return-object
        />
        <div class="d-flex">
          <v-btn
            v-if="ifEdit === 1"
            class="amber lighten-4 black--text"
            elevation="0"
            @click="confirmSaveResource"
          >
            <v-icon left>
              mdi-check
            </v-icon>
            {{ $t('sure') }}
          </v-btn>
          <v-btn
            v-else
            class="amber lighten-4 black--text"
            elevation="0"
            :loading="saveLoading"
            @click="confirmAddResource"
          >
            <v-icon left>
              mdi-check
            </v-icon>
            {{ $t('save') }}
          </v-btn>
          <v-btn
            class="ml-4"
            color="error"
            outlined
            :disabled="saveLoading"
            @click.stop="cancelPickResource"
          >
            <v-icon left>
              mdi-close
            </v-icon>
            {{ $t('cancel') }}
          </v-btn>
        </div>
      </v-container>
    </nice-dialog>
  </div>
</template>

<script>
import PageHeader from '@/views/widget/typography/PageHeader.vue'
import Dish from '@/model/dataLayer/api/dish/Dish'
import PageSubHeader from '@/views/widget/typography/PageSubHeader.vue'
import NiceDialog from '@/views/widget/uiComponent/NiceDialog.vue'
import Resource from '@/model/dataLayer/api/storage/Resource'
import {
  deleteDishConsumption,
  getDishConsumptionList,
  setDishConsumption
} from '@/model/dataLayer/api/storage/storage'
import { groupBy } from 'lodash'
import LoadingProvider from '@/views/widget/LoadingProvider'
import { last } from 'lodash-es'

export default {
  name: 'DishRelationPage',
  components: {
    LoadingProvider,
    PageSubHeader,
    PageHeader,
    NiceDialog
  },
  data: function () {
    return {
      loading: false,
      saveLoading: false,
      editItem: null,
      ifEdit: -1,
      dishList: [],
      searchWord: '',
      showDetailDialog: false,
      selectedDish: null,
      dishResourceList: [],
      showResourcePicker: false,

      resourceList: [],
      relationDict: [],
      unitList: [],

      selectedResource: null,
      selectedUnit: null,
      consumeCount: null
    }
  },
  computed: {
    filteredDishList () {
      const searchWord = this.searchWord.toLowerCase()
      return this.dishList.filter(it => (it.code.toLowerCase() + it.dishName.toLowerCase()).includes(searchWord))
    },
    currentResourceList () {
      return this.selectedDish ? this.relationDict[this.selectedDish.id] ?? [] : []
    }
  },
  watch: {
    selectedResource (val) {
      if (val) {
        this.unitList = val.units
      }
    },
    showResourcePicker (val) {
      if (!val) {
        this.selectedResource = ''
        this.consumeCount = ''
        this.selectedUnit = ''
        this.$nextTick(() => {
          this.configDish(this.selectedDish)
        })
      }
    }
  },
  async mounted () {
    await this.reload()
  },
  methods: {
    async reload () {
      this.loading = true
      this.resourceList = await Resource.getList(true)
      await this.refreshRelationList()
      this.loading = false
    },
    async confirmSaveResource () {
      await deleteDishConsumption(
        this.editItem.id
      )
      await this.confirmAddResource()
    },
    editInfo (item) {
      this.editItem = item
      this.ifEdit = 1
      this.showDetailDialog = false
      this.selectedResource = this.resourceList.find(it => it.id === item.storageItem.id)
      this.consumeCount = item.consumption
      this.selectedUnit = last(item.storageItem.units).id
      this.showResourcePicker = true
    },
    async refreshRelationList () {
      this.relationDict = groupBy(
        await getDishConsumptionList(),
        (it) => it.dishId
      )
      console.log(this.relationDict, 'dict')
      this.dishList = (await Dish.getList()).map(it => {
        it.resource = this.relationDict[it.id] ?? []
        return it
      })
    },
    async configDish (dish) {
      this.selectedDish = dish
      this.showDetailDialog = true
    },
    addAResource () {
      this.showDetailDialog = false
      this.showResourcePicker = true
    },
    cancelPickResource () {
      this.showResourcePicker = false
    },
    async removeResourceFromDish (r) {
      await deleteDishConsumption(
        r.id
      )
      await this.refreshRelationList()
    },
    async confirmAddResource () {
      if (this.saveLoading) {
        return
      }
      this.saveLoading = true

      const res = await setDishConsumption(
        this.selectedDish.id,
        1,
        this.selectedResource.id,
        this.selectedUnit.id,
        this.consumeCount
      )
      this.saveLoading = false
      this.showDetailDialog = true
      this.showResourcePicker = false
      await this.refreshRelationList()
      console.log(res)
    }
  }
}
</script>

<style scoped>

</style>
